/**
 * useAdminMutations — every operator-initiated write action lives here.
 *
 * Extracted from Admin.jsx (Phase 2 P2 #2 decomposition · 2026-06).
 * Behaviour BYTE-IDENTICAL to the inlined version: same window.confirm
 * gates, same `formatApiError` + toast pattern, same re-throw on errors
 * the caller cares about (assignDriver, redispatchRide, forceCancelRide).
 *
 * Contract: every mutation either:
 *   - calls fetchAll() after success (so server-truth replaces the
 *     local UI state), or
 *   - exposes a fresh server response via the dedicated state setter
 *     (e.g. setFareConfig from updateFare) — no follow-up fetch needed
 *     because the PUT response already returned the merged document.
 */
import { useMemo } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export function useAdminMutations({
  fetchAll, fetchHealth,
  setFareConfig, setSurgeConfig, setCycleConfig,
}) {
  // Memoise so callers that depend on referential stability (children
  // using these as effect deps) don't re-render on every parent render.
  return useMemo(() => {
    const deleteUser = async (uid, role) => {
      if (!window.confirm(`Delete this ${role}?`)) return;
      try {
        await api.delete(`/admin/user/${uid}`);
        toast.success("User deleted");
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); }
    };

    const redispatchRide = async (rideId) => {
      try {
        await api.post(`/admin/redispatch/${rideId}`);
        toast.success("Re-dispatched");
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); throw e; }
    };

    const forceCancelRide = async (rideId) => {
      if (!window.confirm("Force-cancel this ride?")) return;
      try {
        await api.post(`/admin/force-cancel/${rideId}`);
        toast.success("Ride cancelled");
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); throw e; }
    };

    // Force-complete is the REVENUE-PRESERVING admin action used when the
    // driver was blocked at the dropoff geofence (GPS missing, address
    // mismatch, passenger asked to be dropped early). It is DIFFERENT
    // from force-cancel:
    //   - status flips to "completed" (not "cancelled")
    //   - payment_status set to "pending" → passenger Pay CTA stays/appears
    //   - driver receipt remains visible with Payment: PENDING
    //   - fare/breakdown/passenger/driver/route preserved verbatim
    //   - admin id + reason + prior phase recorded in ride_events
    // The reason is required server-side (422 returned without one) so we
    // prompt the operator before calling the backend.
    const forceCompleteRide = async (rideId) => {
      const reason = window.prompt(
        "Force-complete this ride? Enter a brief reason (mandatory)\n\n" +
        "Use this when the passenger has travelled and the journey genuinely " +
        "ended, but GPS/geofence/address mismatch prevented driver completion. " +
        "This PRESERVES the fare and activates the passenger payment flow.",
        "GPS/geofence mismatch at dropoff",
      );
      if (reason === null) return; // cancelled
      const cleanReason = (reason || "").trim();
      if (!cleanReason) {
        toast.error("A reason is required for force-complete");
        return;
      }
      try {
        await api.post(`/admin/force-complete/${rideId}`, { override_reason: cleanReason });
        toast.success("Ride force-completed — passenger payment flow now active");
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); throw e; }
    };

    const createBooking = async (payload) => {
      // Re-throws on failure so the NewBookingModal can keep itself open
      // for the operator to fix input and retry (toast is the global fallback).
      try {
        let data;
        try {
          ({ data } = await api.post("/admin/bookings", payload));
        } catch (e) {
          const d = e?.response?.data?.detail;
          if (e?.response?.status !== 409 || d?.code !== "route_unavailable") throw e;
          // Explicit operator approval required; logged server-side on the ride.
          const ok = window.confirm(
            "Google could not calculate a road route for this journey.\n\n"
            + "Create the booking using an APPROXIMATE straight-line distance? "
            + "The fare will be marked as approximate and your approval will be logged.",
          );
          if (!ok) throw e;
          ({ data } = await api.post("/admin/bookings", { ...payload, approximate_distance_override: true }));
        }
        const c = data.customer || {};
        toast.success(
          data.customer_created
            ? `Booked & dispatched — new customer ${c.name} created`
            : `Booked & dispatched for ${c.name}`,
        );
        fetchAll();
        return data;
      } catch (e) { toast.error(formatApiError(e)); throw e; }
    };

    const assignDriver = async (rideId, driverId, driverName) => {
      // row-level error UI (the toast is the global fallback).
      try {
        await api.post(`/admin/assign/${rideId}`, { driver_id: driverId });
        toast.success(`Assigned to ${driverName}`);
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); throw e; }
    };

    const updateFare = async (payload) => {
      try {
        const { data } = await api.put("/admin/fare-config", payload);
        setFareConfig(data);
        toast.success("Fare config updated");
      } catch (e) { toast.error(formatApiError(e)); }
    };

    const updateSurge = async (payload) => {
      try {
        const { data } = await api.put("/admin/surge-config", payload);
        setSurgeConfig(data);
        toast.success("Surge config updated");
      } catch (e) { toast.error(formatApiError(e)); }
    };

    const updateCycle = async (payload) => {
      try {
        const { data } = await api.put("/admin/cycle-config", payload);
        setCycleConfig(data);
        toast.success("Cycle fee updated");
      } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveFixedRoute = async (route) => {
      try {
        if (route.id) {
          const { id, ...rest } = route;
          await api.put(`/admin/fixed-routes/${id}`, rest);
          toast.success(`Updated ${route.name}`);
        } else {
          await api.post("/admin/fixed-routes", route);
          toast.success(`Added ${route.name}`);
        }
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); }
    };

    const deleteFixedRoute = async (id, name) => {
      if (!window.confirm(`Delete fixed route "${name}"?`)) return;
      try {
        await api.delete(`/admin/fixed-routes/${id}`);
        toast.success("Route deleted");
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleAccountCustomer = async (passenger) => {
      try {
        const turnOn = !passenger.is_account_customer;
        let company = passenger.account_company || passenger.name;
        if (turnOn) {
          const input = window.prompt(`Company name for ${passenger.name}?`, company);
          if (input === null) return; // cancelled
          company = input || passenger.name;
        }
        await api.put(`/admin/passenger/${passenger.id}/account`, {
          is_account_customer: turnOn,
          account_company: turnOn ? company : null,
        });
        toast.success(turnOn ? `${passenger.name} is now a corporate account` : "Account flag removed");
        fetchAll();
      } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveCustomer = async (passengerId, { name, phone }) => {
      try {
        await api.patch(`/admin/passenger/${passengerId}`, { name, phone });
        toast.success("Customer updated");
        fetchAll();
      } catch (e) {
        toast.error(formatApiError(e));
        throw e; // keep the edit modal open so the operator can retry
      }
    };

    const resolveHealthFlag = async (rideId) => {
      if (!window.confirm("Acknowledge this stuck ride? It'll be removed from the live list but the ride itself keeps running.")) return;
      try {
        await api.post(`/admin/ride/${rideId}/resolve-health-flag`);
        toast.success("Stuck-flag cleared — ride continues running");
        fetchHealth();
      } catch (e) { toast.error(formatApiError(e)); }
    };

    // Health-tab wrappers — same write action as the Rides tab but with
    // an extra health refresh so the stuck-rides table updates without
    // waiting for the 15s poller.
    const wrappedRedispatch = async (rideId) => {
      await redispatchRide(rideId);
      fetchHealth();
    };
    const wrappedForceCancel = async (rideId) => {
      await forceCancelRide(rideId);
      fetchHealth();
    };
    const wrappedForceComplete = async (rideId) => {
      await forceCompleteRide(rideId);
      fetchHealth();
    };

    return {
      deleteUser, redispatchRide, forceCancelRide, forceCompleteRide, assignDriver,
      createBooking,
      updateFare, updateSurge, updateCycle,
      saveFixedRoute, deleteFixedRoute, toggleAccountCustomer, saveCustomer,
      resolveHealthFlag, wrappedRedispatch, wrappedForceCancel, wrappedForceComplete,
    };
  }, [fetchAll, fetchHealth, setFareConfig, setSurgeConfig, setCycleConfig]);
}
